'use client'

import Image from 'next/image'
import Link from 'next/link'
import type { VeicoloDataVeicolo } from '@/interface/VeicoloDataVeicolo'
import { canOptimizeImageSource, getDisplayImageSource } from '@/lib/image-source'
import { defaultRentToBuyCatalogContent, type RentToBuyCatalogContent } from '@/lib/rent-to-buy-catalog-content'

interface Props {
  product: VeicoloDataVeicolo
  content?: RentToBuyCatalogContent
}

function registrationMonth(value: unknown): string | null {
  if (!value) return null
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('it-IT', { month: '2-digit', year: 'numeric' }).format(date)
}

function priceLabel(value: unknown): string | null {
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount <= 0) return null
  return `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(amount)} €`
}

export default function RentToBuyVehicleCard({ product, content = defaultRentToBuyCatalogContent }: Props) {
  const href = product.slug ? `/noleggio-b2b/${product.slug}` : '/rent-to-buy'
  const imageSrc = getDisplayImageSource(product.foto, '/autoinarrivo.webp', {
    slug: product.slug,
    articolo: product.articolo,
  })
  const netPrice = priceLabel(product.prezzoSenzaIva)
  const grossPrice = priceLabel(product.prezzo)
  const price = netPrice ?? grossPrice
  const badges = [
    product.km !== null && product.km !== undefined && product.km !== '' && Number.isFinite(Number(product.km))
      ? `KM ${new Intl.NumberFormat('it-IT').format(Number(product.km))}`
      : null,
    registrationMonth(product.immatricolazione),
    product.alimentazione || null,
  ].filter((badge): badge is string => Boolean(badge))

  return (
    <article className="flex min-h-[536px] min-w-0 flex-col overflow-hidden rounded-[24px] border border-[#e3e9f1] bg-white shadow-[0_10px_26px_rgba(5,15,51,0.08)] xl:min-h-[508px]">
      <div className="relative aspect-[316/220] shrink-0 overflow-hidden bg-[#f3f6fa]">
        <Link href={href} scroll aria-label={`Apri ${product.marca} ${product.modello}`} className="block h-full w-full">
          <Image
            src={imageSrc}
            alt={`${product.marca} ${product.modello}`}
            fill
            sizes="(max-width: 767px) calc(100vw - 40px), (max-width: 1279px) 50vw, 316px"
            className="object-cover"
            unoptimized={!canOptimizeImageSource(imageSrc)}
          />
        </Link>
        <div className="pointer-events-none absolute left-4 top-4 flex max-w-[calc(100%-76px)] flex-wrap gap-1.5">
          {badges.map((badge, index) => <span key={`${badge}-${index}`} className="rounded-full bg-white/90 px-3 py-1 text-[13px] font-bold text-[#10223a]">{badge}</span>)}
        </div>
        <span aria-hidden="true" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white">
          <Image src="/assets/rent-to-buy/icon-heart-card.svg" width={18} height={16} alt="" />
        </span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6 pt-5 xl:px-[23px] xl:pb-[23px]">
        <div className="min-h-[62px]">
          <p className="text-sm font-bold tracking-[0.02em] text-[#5b6b82]">{product.marca}</p>
          <h3 className="mt-0.5 line-clamp-2 text-[17px] font-bold leading-[1.3] text-[#0b1b33]">{product.modello}</h3>
        </div>
        <div className="border-t border-[#e3e9f1] pt-4">
          <p className="text-xs font-bold text-[#4a586f]">{content.priceLabel}</p>
          <div className="mt-0.5 flex items-baseline gap-2">
            <p className="text-[30px] font-extrabold tracking-[-0.02em] text-[#10223a]">{price ?? 'Su richiesta'}</p>
            {price && <span className="text-[13px] font-semibold text-[#4a586f]">{netPrice ? '+ IVA' : 'IVA inclusa'}</span>}
          </div>
        </div>
        <Link href={href} scroll className="mt-auto flex min-h-[49px] items-center justify-center gap-2 rounded-[13px] bg-[#0b1b33] px-3 text-center text-[15px] font-bold text-white hover:bg-[#183250]">
          <Image src="/assets/rent-to-buy/icon-calculator-card.svg" width={19} height={19} alt="" aria-hidden="true" />
          {content.calculateLabel}
        </Link>
      </div>
    </article>
  )
}
